import React from 'react';
import px from 'prop-types';
import cx from 'classnames';
import * as PRODUCT from '~config/product';
import { Translation } from 'Common/components/localization';
import { Spinner } from 'Common/components/ui';
import { useTranslation } from 'Common/hooks';

const MOBILE_BREAKPOINT = 767;
const DRAG_THRESHOLD = 40;

export default function CatalogCompareTray({
    items,
    isLoading,
    comparePageUrl,
    onRemoveFromCompareList,
    onClearCompareList,
    className,
}) {
    const lblRemove = useTranslation('Commerce.Order.Cart.RemoveItem.Label');

    const visibleItems = Array.isArray(items) ? items.slice(0, 4) : [];
    const emptySlotCount = Math.max(4 - visibleItems.length, 0);

    const [isMobileExpanded, setIsMobileExpanded] = React.useState(false);
    const [isMobileViewport, setIsMobileViewport] = React.useState(false);

    const dragStartYRef = React.useRef(null);

    React.useEffect(() => {
        const updateViewport = () => {
            const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

            setIsMobileViewport(isMobile);

            if (!isMobile) {
                setIsMobileExpanded(false);
            }
        };

        updateViewport();
        window.addEventListener('resize', updateViewport);

        return () => {
            window.removeEventListener('resize', updateViewport);
        };
    }, []);

    const onClickRemove = React.useCallback((code) => () => onRemoveFromCompareList(code), [onRemoveFromCompareList]);

    const onClickClear = React.useCallback(() => {
        onClearCompareList();
    }, [onClearCompareList]);

    const shouldRender = isLoading || visibleItems.length > 0;

    const onHandleToggle = React.useCallback(() => {
        if (isMobileViewport) {
            setIsMobileExpanded((current) => !current);
        }
    }, [isMobileViewport]);

    const onHandlePointerDown = React.useCallback((event) => {
        dragStartYRef.current = event.clientY;
    }, []);

    const onHandlePointerUp = React.useCallback((event) => {
        if (dragStartYRef.current == null) {
            return;
        }

        const deltaY = event.clientY - dragStartYRef.current;

        if (deltaY <= -DRAG_THRESHOLD) {
            setIsMobileExpanded(true);
        } else if (deltaY >= DRAG_THRESHOLD) {
            setIsMobileExpanded(false);
        }

        dragStartYRef.current = null;
    }, []);

    if (!shouldRender) {
        return null;
    }

    return (
        <aside
            className={cx('CatalogCompareTray', className, {
                'CatalogCompareTray--loading': isLoading,
                'CatalogCompareTray--visible': shouldRender,
                'CatalogCompareTray--mobile': isMobileViewport,
                'CatalogCompareTray--mobile-expanded': isMobileViewport && isMobileExpanded,
                'CatalogCompareTray--mobile-collapsed': isMobileViewport && !isMobileExpanded,
            })}
            aria-label="Compare selected products"
        >
            <div className="CatalogCompareTray__inner">
                {isMobileViewport ? (
                    <button
                        type="button"
                        className="CatalogCompareTray__drag-handle-button"
                        onClick={onHandleToggle}
                        onPointerDown={onHandlePointerDown}
                        onPointerUp={onHandlePointerUp}
                        aria-expanded={isMobileExpanded}
                        aria-label={isMobileExpanded ? 'Collapse compare tray' : 'Expand compare tray'}
                    >
                        <span className="CatalogCompareTray__drag-handle" />
                    </button>
                ) : null}

                <div className="CatalogCompareTray__summary">
                    <h3 className="CatalogCompareTray__title">
                        <Translation id="Commerce.Product.Compare.SectionHeader" />
                    </h3>

                    <div className="CatalogCompareTray__meta">
                        {isLoading ? null : (
                            <span className="CatalogCompareTray__count">{visibleItems.length} / 4</span>
                        )}
                    </div>
                </div>

                <div className="CatalogCompareTray__content">
                    {isLoading ? (
                        <div className="CatalogCompareTray__loading">
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className="CatalogCompareTray__list-wrapper">
                                <ul className="CatalogCompareTray__list">
                                    {visibleItems.map((item) => (
                                        <li key={item.Code} className="CatalogCompareTray__slot">
                                            <div className="CatalogCompareTray__item">
                                                <button
                                                    type="button"
                                                    className="CatalogCompareTray__remove"
                                                    onClick={onClickRemove(item.Code)}
                                                    aria-label={lblRemove}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>

                                                <div className="CatalogCompareTray__media">
                                                    {item.CatalogEntity?.DefaultImageUrl ? (
                                                        <img
                                                            src={`${item.CatalogEntity.DefaultImageUrl}?format=png&height=100&width=100${
                                                                PRODUCT.TRANSFORM_PRODUCT_IMAGE_BACKGROUND
                                                                    ? '&transBg=true'
                                                                    : ''
                                                            }`}
                                                            alt={item.DisplayName}
                                                            className="CatalogCompareTray__image"
                                                        />
                                                    ) : (
                                                        <div className="CatalogCompareTray__image CatalogCompareTray__image--placeholder" />
                                                    )}
                                                </div>

                                                <div className="CatalogCompareTray__details">
                                                    {item.BrandName ? (
                                                        <div className="CatalogCompareTray__brand">
                                                            {item.BrandName}
                                                        </div>
                                                    ) : null}

                                                    <div className="CatalogCompareTray__name">
                                                        {item.ProductUrl ? (
                                                            <a href={item.ProductUrl}>{item.DisplayName}</a>
                                                        ) : (
                                                            item.DisplayName
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}

                                    {Array.from({ length: emptySlotCount }).map((_, index) => (
                                        <li
                                            key={`empty-slot-${index}`}
                                            className="CatalogCompareTray__slot CatalogCompareTray__slot--empty"
                                            aria-hidden="true"
                                        >
                                            <div className="CatalogCompareTray__placeholder"></div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="CatalogCompareTray__actions">
                                <button
                                    type="button"
                                    className={cx(
                                        'btn-secondary CatalogCompareTray__button',
                                        isMobileViewport ? 'btn-sm' : 'btn',
                                        {
                                            disabled: visibleItems.length < 1,
                                        }
                                    )}
                                    onClick={onClickClear}
                                >
                                    <Translation id="Commerce.Product.Compare.Clear" />
                                </button>

                                <a
                                    href={comparePageUrl}
                                    className={cx(
                                        'btn-primary CatalogCompareTray__button',
                                        isMobileViewport ? 'btn-sm' : 'btn',
                                        {
                                            disabled: visibleItems.length < 2,
                                        }
                                    )}
                                    aria-disabled={visibleItems.length < 2}
                                    onClick={(event) => {
                                        if (visibleItems.length < 2) {
                                            event.preventDefault();
                                        }
                                    }}
                                >
                                    <Translation id="Commerce.Product.Compare.ViewAll" />
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </aside>
    );
}

CatalogCompareTray.propTypes = {
    items: px.arrayOf(
        px.shape({
            Code: px.string.isRequired,
            DisplayName: px.string.isRequired,
            CatalogEntity: px.shape({
                DefaultImageUrl: px.string,
            }),
            ProductUrl: px.string,
            BrandName: px.string,
        })
    ).isRequired,
    isLoading: px.bool,
    comparePageUrl: px.string.isRequired,
    onRemoveFromCompareList: px.func.isRequired,
    onClearCompareList: px.func.isRequired,
    className: px.string,
};

CatalogCompareTray.defaultProps = {
    isLoading: false,
    maxItems: 4,
    className: undefined,
};
